<template>
  <v-card class="measuring-recurrency">
    <v-card-text>
      <v-row>
        <v-col cols="6" class="py-0 pr-8">
          <h3 class="measuring-recurrency-title">Data e hora de envio</h3>
          <p class="measuring-recurrency-subtitle">Selecione a data e hora do envio das mensagens ao consumidor.</p>
          <v-container class="pa-0" fluid>
            <v-row>
              <v-col class="pb-0">
                <div class="label-box" />
                <tooltip align="right" :disabled="!isIndustryCampaign" :message="tooltipMessage">
                  <date-picker
                    class="date-picker"
                    :min="minDate"
                    :date.sync="campaign.message.send_date"
                    :error-messages="errorSendDate"
                    :disabled="disabled"
                    outlined
                    label="Data do disparo"
                    @input="
                      campaign.message.send_time = ''
                      campaign.initial_date = ''
                      campaign.final_date = ''
                    "
                  />
                </tooltip>
              </v-col>
              <v-col class="measuring-recurrency-time pb-0">
                <tooltip align="right" :disabled="!isIndustryCampaign" :message="tooltipMessage">
                  <mf-time-picker
                    v-model="campaign.message.send_time"
                    :min="calculatedMinTime"
                    :max="maxTime"
                    outline
                    label="Hora do disparo"
                    :error-messages="errorSendTime"
                    :disabled="disabled"
                    hint="O envio das mensagens pode iniciar até 45 minutos após o horário configurado."
                    persistent-hint
                    outlined
                  />
                </tooltip>
                <div class="label-box">
                  <span>Horário de Brasília: (GMT-03:00)</span>
                </div>
              </v-col>
            </v-row>
            <div v-if="isWhatsappCampaign" class="measuring-recurrency-warning">
              <mf-icon fill large color="vermelho-0">warning</mf-icon>
              <span>
                Caso ocorra algum problema com o envio da campanha, continuaremos tentando disparar até o final do dia do disparo. Depois desse período, o envio
                será suspenso.
              </span>
            </div>
          </v-container>
          <send-datetime-configuration
            v-if="!isWhatsappCampaign"
            :error="getSafe(() => validations[validationProperty].frequency.interval.$error)"
            :frequency-type.sync="campaign.frequency.type"
            :frequency-interval.sync="campaign.frequency.interval"
            :disabled="disabled"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipMessage"
          />
        </v-col>
        <v-col cols="6" class="py-0 pl-8">
          <measuring-period-configuration
            height="185px"
            :initial-date.sync="campaign.initial_date"
            :send-date="campaign.message.send_date"
            :final-date.sync="campaign.final_date"
            :initial-date-error="getSafe(() => validations[validationProperty].initial_date.$error)"
            :final-date-error="getSafe(() => validations[validationProperty].final_date.$error)"
            :disabled="disabled"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipMessage"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { DateTime } from 'luxon'

export default {
  components: {
    MeasuringPeriodConfiguration: () => import('@/modules/campanhas/views/components/edit/MeasuringPeriodConfiguration.vue'),
    SendDatetimeConfiguration: () => import('@/modules/campanhas/views/components/edit/SendDatetimeConfiguration.vue'),
    DatePicker: () => import('@/components/DatePicker.vue'),
    Tooltip: () => import('@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip.vue'),
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    validations: {
      type: Object,
      default: () => {},
    },
    isWhatsappCampaign: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tooltipMessage: {
      type: String,
      default: '',
    },
    minTime: {
      type: String,
      default: '',
    },
    maxTime: {
      type: String,
      default: '',
    },
    isIndustryCampaign: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    campaign: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    minDate() {
      return DateTime.now().toFormat('yyyy-MM-dd')
    },
    errorSendDate() {
      if (!this.validations[this.validationProperty].message || !this.validations[this.validationProperty].message.send_date) return ''
      if (this.validations[this.validationProperty].message.send_date.$error) {
        if (!this.validations[this.validationProperty].message.send_date.required) return 'Selecione uma data de envio.'
        if (!this.validations[this.validationProperty].message.send_date.isFuture) return 'A data de envio deve ser no futuro.'
      }
      return ''
    },
    calculatedMinTime() {
      const defaultMinTime = DateTime.now().toFormat('HH:mm')
      if (this.campaign.message.send_date === DateTime.now().toFormat('yyyy-MM-dd')) {
        return this.minTime ? Math.max(this.minTime, defaultMinTime) : defaultMinTime
      }
      return this.minTime ?? ''
    },
    errorSendTime() {
      if (!this.validations[this.validationProperty].message || !this.validations[this.validationProperty].message.send_time) return ''
      if (this.validations[this.validationProperty].message.send_time.$error) {
        if (!this.validations[this.validationProperty].message.send_time.required) return 'Selecione a hora de envio.'
        if (!this.validations[this.validationProperty].message.send_time.timeIsFuture) return 'Não é possível ativar campanha com hora anterior a atual.'
      }
      return ''
    },
    validationProperty() {
      if (Object.keys(this.validations).length > 0) {
        return Object.keys(this.validations)[0]
      } else {
        return 'campaign'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.measuring-recurrency {
  &-title {
    margin-bottom: 8px;
    font-size: 18px;
    font-weight: 400;
    color: $cinza-d10;
  }
  &-subtitle {
    font-weight: 400;
    font-size: 14px;
    color: $cinza-0;
    margin: 0 !important;
    padding-bottom: 8px;
  }
  &-time {
    position: relative;
  }
  .label-box {
    position: absolute;
    top: -8px;
    right: 12px;
    min-height: 24px;
    span {
      display: block;
      margin-bottom: 0;
      text-align: right;
      opacity: 0.8;
      font-size: 11px;
      font-weight: 500;
      line-height: 11px;
      letter-spacing: 0.18px;
    }
  }
  &-warning {
    display: flex;
    column-gap: 12px;
    span {
      font-weight: 400;
      font-size: 14px;
      color: $cinza-0;
    }
  }
}
</style>
